/**
 * Packaging OS — DataState (Phase 1O-B).
 *
 * Renders the non-success branches of an ApiResult uniformly:
 *   loading   → info pill
 *   401/403   → sign-in / preview CTA
 *   429       → rate-limit pill (with retry-after)
 *   5xx       → service-unavailable pill
 *   network   → network pill
 *   ok        → render(data) callback
 *
 * Empty state handled via emptyWhen() predicate when ok.
 */

import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ApiResult } from "../api/client";
import { Button } from "./Button";
import { StatusPill } from "./StatusPill";
import type { UiStrings } from "../i18n/strings";

interface DataStateProps<T> {
  state: { kind: "loading" } | { kind: "loaded"; result: ApiResult<T> };
  t: UiStrings;
  emptyWhen?: (data: T) => boolean;
  emptyTitle?: string;
  emptyBody?: string;
  renderOk: (data: T, requestId: string | null) => ReactNode;
}

export function DataState<T>({
  state,
  t,
  emptyWhen,
  emptyTitle,
  emptyBody,
  renderOk,
}: DataStateProps<T>): JSX.Element {
  const navigate = useNavigate();

  if (state.kind === "loading") {
    return (
      <div className="po-row">
        <StatusPill tone="info" label={t.whoAmIChecking} />
      </div>
    );
  }

  const r = state.result;
  switch (r.outcome) {
    case "ok": {
      const data = r.data as T;
      const persisted = (data as { persisted_run?: { artifact_id: string } })
        ?.persisted_run;
      if (emptyWhen && emptyWhen(data)) {
        return (
          <div className="po-empty">
            <h3 className="po-empty__title">
              {emptyTitle ?? t.catalogEmptyTitle}
            </h3>
            <p>{emptyBody ?? t.catalogEmptyBody}</p>
          </div>
        );
      }
      return (
        <>
          {persisted ? (
            <div className="po-row" data-testid="persisted-run-banner">
              <StatusPill tone="info" label={t.runSavedBanner} />
              <Link to={`/app/artifacts?focus=${persisted.artifact_id}`}>
                {t.runSavedArtifactLink}
              </Link>
            </div>
          ) : null}
          {renderOk(data, r.requestId)}
        </>
      );
    }
    case "auth-missing":
    case "auth-invalid":
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="warn" label={t.errAuthMissing} />
          </div>
          <div className="po-row">
            <Button variant="primary" onClick={() => navigate("/login")}>
              {t.ctaSignInWithGoogle}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/preview")}>
              {t.ctaRequestAccess}
            </Button>
          </div>
          {r.requestId && <RequestIdBadge t={t} requestId={r.requestId} />}
        </div>
      );
    case "permission-denied":
      return (
        <section
          className="po-card po-card--preview-restricted"
          role="status"
          aria-live="polite"
          data-testid="po-preview-restricted"
        >
          <div className="po-row">
            <StatusPill tone="warn" label={t.catalogPreviewRestrictedTitle} />
          </div>
          <p className="po-card__body">{t.catalogPreviewRestrictedBody}</p>
          <div className="po-row">
            <Button variant="ghost" onClick={() => navigate("/preview")}>
              {t.catalogPreviewRestrictedCta}
            </Button>
          </div>
          {r.requestId && <RequestIdBadge t={t} requestId={r.requestId} />}
        </section>
      );
    case "rate-limited":
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="warn" label={t.errRateLimited} />
            {r.retryAfterSeconds && (
              <span className="po-request-id">({r.retryAfterSeconds}s)</span>
            )}
          </div>
          {r.requestId && <RequestIdBadge t={t} requestId={r.requestId} />}
        </div>
      );
    case "service-unavailable":
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="err" label={t.errServiceUnavailable} />
          </div>
          {r.requestId && <RequestIdBadge t={t} requestId={r.requestId} />}
        </div>
      );
    case "network-error":
      return (
        <div className="po-row">
          <StatusPill tone="err" label={t.errNetwork} />
        </div>
      );
    case "validation-error":
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="err" label={r.errorMessage ?? t.errUnknown} />
          </div>
          {r.requestId && <RequestIdBadge t={t} requestId={r.requestId} />}
        </div>
      );
    default:
      return (
        <div className="po-stack">
          <div className="po-row">
            <StatusPill tone="err" label={t.errUnknown} />
          </div>
          {r.requestId && <RequestIdBadge t={t} requestId={r.requestId} />}
        </div>
      );
  }
}

interface RequestIdBadgeProps {
  t: UiStrings;
  requestId: string;
}

export function RequestIdBadge({
  t,
  requestId,
}: RequestIdBadgeProps): JSX.Element {
  return (
    <p className="po-request-id">
      <span>{t.errRequestIdLabel} </span>
      <span className="dir-ltr-island">{requestId}</span>
    </p>
  );
}
